import React, { useState, useEffect } from 'react';
import { X, Save, Calendar, DollarSign, FileText, Image, Users, MapPin } from 'lucide-react';
import { supabase } from '../../../utils/supabaseClient';

const SubmissionModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  submission = null, 
  galleries = [], 
  collections = [], 
  isEditing = false 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    theme: '',
    submission_price: '',
    submission_deadline: '',
    featured: false,
    gallery_id: '',
    collection_id: '',
    submission_guidelines: '',
    max_allowed: '',
    callout_headline: '',
    artistic_guidelines: '',
    promotion_guidelines: '',
    important_dates: '',
    cover_image: '',
    stripe_product_id: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data when editing
  useEffect(() => {
    if (submission && isEditing) {
      setFormData({
        title: submission.title || '',
        theme: submission.theme || '',
        submission_price: submission.submission_price || '',
        submission_deadline: submission.submission_deadline ? submission.submission_deadline.split('T')[0] : '',
        featured: submission.featured || false,
        gallery_id: submission.gallery_id || '',
        collection_id: submission.collection_id || '',
        submission_guidelines: submission.submission_guidelines || '',
        max_allowed: submission.max_allowed || '',
        callout_headline: submission.callout_headline || '',
        artistic_guidelines: submission.artistic_guidelines || '',
        promotion_guidelines: submission.promotion_guidelines || '',
        important_dates: submission.important_dates || '',
        cover_image: submission.cover_image || '',
        stripe_product_id: submission.stripe_product_id || ''
      });
    }
  }, [submission, isEditing]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle textarea changes
  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.theme.trim()) newErrors.theme = 'Theme is required';
    if (!formData.submission_price || formData.submission_price <= 0) newErrors.submission_price = 'Valid price is required';
    if (!formData.gallery_id) newErrors.gallery_id = 'Gallery is required';
    if (!formData.collection_id) newErrors.collection_id = 'Collection is required';
    if (!formData.submission_guidelines.trim()) newErrors.submission_guidelines = 'Guidelines are required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);

      const submissionData = {
        ...formData,
        submission_price: parseFloat(formData.submission_price),
        max_allowed: formData.max_allowed ? parseInt(formData.max_allowed) : null,
        updated_at: new Date().toISOString()
      };

      if (isEditing) {
        // Update existing submission
        const { error } = await supabase
          .from('submissions_details')
          .update(submissionData)
          .eq('submission_id', submission.submission_id);

        if (error) throw error;
      } else {
        // Create new submission
        submissionData.created_at = new Date().toISOString();
        submissionData.submission_id = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const { error } = await supabase
          .from('submissions_details')
          .insert([submissionData]);

        if (error) throw error;
      }

      onSave();
    } catch (error) {
      console.error('Error saving submission:', error);
      alert('Failed to save submission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setFormData({
        title: '',
        theme: '',
        submission_price: '',
        submission_deadline: '',
        featured: false,
        gallery_id: '',
        collection_id: '',
        submission_guidelines: '',
        max_allowed: '',
        callout_headline: '',
        artistic_guidelines: '',
        promotion_guidelines: '',
        important_dates: '',
        cover_image: '',
        stripe_product_id: ''
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Submission' : 'Create New Submission'}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Submission Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter submission title"
              />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Theme *
              </label>
              <input
                type="text"
                name="theme"
                value={formData.theme}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  errors.theme ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter submission theme"
              />
              {errors.theme && <p className="mt-1 text-sm text-red-600">{errors.theme}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Submission Price ($) *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="number"
                  name="submission_price"
                  value={formData.submission_price}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.submission_price ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
              </div>
              {errors.submission_price && <p className="mt-1 text-sm text-red-600">{errors.submission_price}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Submission Deadline
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  name="submission_deadline"
                  value={formData.submission_deadline}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label className="ml-2 text-sm text-gray-700">Mark as featured submission</label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Allowed
              </label>
              <input
                type="number"
                name="max_allowed"
                value={formData.max_allowed}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-500 focus:border-transparent"
                placeholder="Unlimited"
              />
            </div>
          </div>

          {/* Gallery and Collection */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gallery *
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  name="gallery_id"
                  value={formData.gallery_id}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.gallery_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Gallery</option>
                  {galleries.map(gallery => (
                    <option key={gallery.id} value={gallery.id}>
                      {gallery.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.gallery_id && <p className="mt-1 text-sm text-red-600">{errors.gallery_id}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Collection *
              </label>
              <div className="relative">
                <Image className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  name="collection_id"
                  value={formData.collection_id}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors.collection_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Collection</option>
                  {collections.map(collection => (
                    <option key={collection.collection_id} value={collection.collection_id}>
                      {collection.title}
                    </option>
                  ))}
                </select>
              </div>
              {errors.collection_id && <p className="mt-1 text-sm text-red-600">{errors.collection_id}</p>}
            </div>
          </div>

          {/* Callout Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Callout Headline
              </label>
              <input
                type="text"
                name="callout_headline"
                value={formData.callout_headline}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter callout headline"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cover Image URL
              </label>
              <input
                type="url"
                name="cover_image"
                value={formData.cover_image}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>

          {/* Guidelines and Requirements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Submission Guidelines *
            </label>
            <textarea
              name="submission_guidelines"
              value={formData.submission_guidelines}
              onChange={handleTextareaChange}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                errors.submission_guidelines ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter detailed submission guidelines..."
            />
            {errors.submission_guidelines && <p className="mt-1 text-sm text-red-600">{errors.submission_guidelines}</p>}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Artistic Guidelines
              </label>
              <textarea
                name="artistic_guidelines"
                value={formData.artistic_guidelines}
                onChange={handleTextareaChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter artistic guidelines..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Promotion Guidelines
              </label>
              <textarea
                name="promotion_guidelines"
                value={formData.promotion_guidelines}
                onChange={handleTextareaChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter promotion guidelines..."
              />
            </div>
          </div>

          {/* Important Dates */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Important Dates
            </label>
            <textarea
              name="important_dates"
              value={formData.important_dates}
              onChange={handleTextareaChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter important dates and deadlines..."
            />
          </div>

          {/* Stripe Integration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stripe Product ID
            </label>
            <input
              type="text"
              name="stripe_product_id"
              value={formData.stripe_product_id}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="prod_xxxxxxxxxxxxx"
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isEditing ? 'Update Submission' : 'Create Submission'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmissionModal;
